import { z } from "zod";
import { TypedEventEmitter } from "../utils";
import { ConnectionManager } from "./connection";
import { SignalingProvider } from "./signaling/SignalingProvider";

const packetSchema = z.object({
	event: z.string(),
	data: z.unknown(),
}, {
	message: "Invalid packet",
});

// there is probably a way to avoid all the type hacking here :)
export type PacketTypes = Record<string, z.ZodTypeAny>;

export type InferPacketTypes<Packets extends PacketTypes> = {
	[K in keyof Packets]: Packets[K] extends z.ZodTypeAny
	? z.infer<Packets[K]> : never
};

export class Multiplayer<Packets extends PacketTypes> extends TypedEventEmitter<{
	hosted: null
	joined: null
	connected: null
	disconnected: null
}> {
	public readonly packets: TypedEventEmitter<InferPacketTypes<Packets>>; // this is its own event emitter because i wanted to separate user space events/packets from library/system events

	private readonly packetTypes: Packets;
	private _connection: ConnectionManager | null = null;

	public get connection() {
		if (!this._connection) {
			throw new Error("Not connected to a room");
		}
		return this._connection;
	}
	private set connection(connection: ConnectionManager) {
		this._connection = connection;
	}

	constructor(private readonly signalingProvider: SignalingProvider, packetTypes: Packets) {
		super();
		this.packetTypes = packetTypes
		this.packets = new TypedEventEmitter<InferPacketTypes<Packets>>();
	}

	public packetType<KeyType extends string, SchemaType extends z.ZodTypeAny>(event: KeyType, schema: SchemaType): Multiplayer<Packets & Record<KeyType, SchemaType>> {
		return new Multiplayer<Packets & Record<KeyType, SchemaType>>(
			this.signalingProvider,
			{
				...this.packetTypes,
				[event]: schema,
			} as Packets & Record<KeyType, SchemaType>
		);
	}

	public destroy() {
		// TODO: implement destroy method
		throw new Error("destroy not implemented");
	}

	public async host() {
		this.connection = await ConnectionManager.host(this.signalingProvider);
		this.connection.on("onMessage", this.messageHandler);
		this.emit("hosted", null);
		this.emit("connected", null);
		return this.connection.roomId;
	}

	public async join(roomId: string) {
		this.connection = await ConnectionManager.join(roomId, this.signalingProvider);
		this.connection.on("onMessage", this.messageHandler);
		this.emit("joined", null);
		this.emit("connected", null);
	}

	public async send<T extends keyof Packets>(
		event: T,
		data: z.infer<Packets[T]>,
		mode: 'remoteOnly' | 'both' | 'localOnly' = 'remoteOnly'
	) {
		if (!this.connection) {
			throw new Error("Not connected to a room");
		}

		const packet = {
			event,
			data: this.packetTypes[event].parse(data),
		};

		switch (mode) {
			case 'remoteOnly':
				this.connection.send(packet);
				break;
			case 'localOnly':
				this.packets.emit(event, data);
				break;
			case 'both':
				this.connection.send(packet);
				this.packets.emit(event, data);
				break;

		}
	}

	private messageHandler = (message: unknown) => {
		const packet = packetSchema.parse(message);

		const { event, data } = packet;
		if (this.packetTypes[event]) {
			this.packets.emit(event, this.packetTypes[event].parse(data));
		} else {
			console.warn(`Unknown packet type: ${event}`);
		}
	}
}


